import { Box } from 'oskcomponents';
import { noop } from 'oskcore';
import React from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { ClickableLogo, ModeButton } from '~/atoms';
import { GlobalZIndex } from '~/constants';
import { useCurrentApp } from 'oskcore/src/hooks';
import { getProgramId } from '~/utils';
import { useToggles } from '~/hooks/useToggles';

type ActiveMode =
    | 'archive'
    | 'tasking'
    | 'tasking-dashboard'
    | 'orders'
    | 'checkout'
    | 'monitor'
    | 'reports'
    | 'findings'
    | 'satellite-track'
    | 'notation'
    | 'orders-admin'
    | 'external-orders';

type ActiveModeSwitcherProps = {
    /** CSS Class for styling the container */
    className?: string;
};

const ActiveModeSwitcher = styled(({ className }: ActiveModeSwitcherProps) => {
    const { pathname } = useLocation();
    const activeApp = useCurrentApp();
    const programId = useParams()?.programId ?? getProgramId();
    const toggles = useToggles();

    let activeMode: ActiveMode = 'archive';

    // Use this more advanced form of rule-based matching so we can
    // prioritize more easily. For example:
    //
    // startsWith('/monitor/') would match /monitor/reports
    // But those are two separate paths and the latter should
    // overrule the former.
    if (pathname.startsWith('/tasking')) {
        activeMode = 'tasking';
    } else if (pathname === '/tasking/dashboard') {
        activeMode = 'tasking-dashboard';
    } else if (pathname.endsWith('/track')) {
        activeMode = 'satellite-track';
    } else if (pathname.includes('/monitor/reports')) {
        activeMode = 'reports';
    } else if (pathname.includes('/monitor/findings')) {
        activeMode = 'findings';
    } else if (pathname.includes('/monitor')) {
        activeMode = 'monitor';
    } else if (pathname.includes('/checkout')) {
        activeMode = 'checkout';
    } else if (pathname.startsWith('/orders')) {
        activeMode = 'orders';
    } else if (pathname.includes('/tools/orders')) {
        activeMode = 'orders-admin';
    } else if (pathname.startsWith('/myorders')) {
        activeMode = 'external-orders';
    } else if (pathname === '/' || pathname.includes('/map')) {
        activeMode = 'archive';
    }

    return (
        <>
            <Box
                col
                style={{ zIndex: GlobalZIndex.ActionBar + 100999999 }}
                className={className}
                data-testid="ActiveModeSwitcher"
            >
                <ClickableLogo />
                {(() => {
                    switch (activeApp) {
                        case 'data':
                            return (
                                <>
                                    <ModeButton
                                        data-mode="archive"
                                        icon={'search'}
                                        location={`/map`}
                                        active={activeMode === 'archive'}
                                        tooltip="Search our archive of imagery"
                                        label="Archive Search"
                                    />
                                    {toggles.showTasking() && (
                                        <ModeButton
                                            data-mode="tasking"
                                            icon={'crosshair'}
                                            location="/tasking"
                                            active={activeMode === 'tasking'}
                                            scale={100}
                                            tooltip="Create a new tasking order"
                                            label="Task Order"
                                        />
                                    )}
                                    <ModeButton
                                        data-mode="external-orders"
                                        icon={'table'}
                                        location={`/myorders`}
                                        active={activeMode === 'external-orders'}
                                        tooltip="View the status of past and current orders"
                                        label={toggles.showInternalOrders() ? 'External Orders' : 'Order Dashboard'}
                                    />
                                    {toggles.showInternalOrders() && (
                                        <ModeButton
                                            data-mode="orders"
                                            icon={'table'}
                                            location={`/orders`}
                                            active={activeMode === 'orders'}
                                            tooltip="View the status of past and current orders"
                                            label="Internal Orders"
                                        />
                                    )}
                                    <ModeButton
                                        data-mode="satellite"
                                        icon="satellite"
                                        location="/data/track"
                                        active={activeMode === 'satellite-track'}
                                        tooltip="Track the GHOSt constellation"
                                        label="GHOSt Tracker"
                                    />
                                </>
                            );
                        case 'monitor':
                            return (
                                <>
                                    <ModeButton
                                        data-mode="monitor"
                                        icon={'quilt'}
                                        location={`/program/${programId}/monitor`}
                                        active={activeMode === 'monitor'}
                                        tooltip="View the summary of your assets and accompanying findings"
                                        label="Summary"
                                    />
                                    <ModeButton
                                        data-mode="findings"
                                        icon={'alert'}
                                        location={`/program/${programId}/monitor/findings`}
                                        active={activeMode === 'findings'}
                                        tooltip="View all findings across your assets"
                                        label="Findings"
                                    />
                                    <ModeButton
                                        data-mode="reports"
                                        icon={'report'}
                                        location={`/program/${programId}/monitor/reports`}
                                        active={activeMode === 'reports'}
                                        tooltip="View the generated reports for your assets"
                                        label="Reports"
                                    />
                                    <ModeButton
                                        data-mode="satellite"
                                        icon="satellite"
                                        location="/monitor/track"
                                        active={activeMode === 'satellite-track'}
                                        tooltip="Track the GHOSt constellation"
                                        label="GHOSt Tracker"
                                    />
                                </>
                            );
                        case 'tools':
                            return (
                                <>
                                    <ModeButton
                                        data-mode="orders-admin"
                                        icon="table"
                                        location="/tools/orders"
                                        active={activeMode === 'orders-admin'}
                                        tooltip="Order Administration"
                                        label="Order Admin"
                                    />
                                    <ModeButton
                                        data-mode="satellite"
                                        icon="satellite"
                                        location="/tools/track"
                                        active={activeMode === 'satellite-track'}
                                        tooltip="Track the GHOSt constellation"
                                        label="GHOSt Tracker"
                                    />
                                </>
                            );
                        default:
                            return <></>;
                    }
                })()}
            </Box>
            {/* Do not remove. This adds necessary padding since the actual button bar is floating */}
            <Box w={73} style={{ minWidth: '73px' }} h={'100%'}>
                &nbsp;
            </Box>
        </>
    );
})`
    & {
        box-shadow: 3px 0px 4px rgba(0, 0, 0, 0.25);
        align-items: center;
        min-width: 73px;
        width: 73px;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background-color: ${(props: any) => props.theme.colors.primary.bg};
    }
`;

export { ActiveModeSwitcher };
export default connect(noop, noop)(ActiveModeSwitcher);
